import { useEffect, useMemo, useState } from 'react';
import type { ISelectOption, IModelInstanceOptions } from './Filter';
import transformKeyToJsonLogic from '../../utils/transformKeysToJsonLogic';
import useFetchModelInstancesFilterValues from '../../hooks/api/useFetchModelInstancesFilterValues';
import { useDebounce, useLazyLoad } from '../../../hooks';
import useFetchModelInstancesCursorPagination from '../../hooks/api/useFetchModelInstancesCursorPagination';
import {
  buildFiltersParam,
  buildSearchJsonLogic,
} from '../../utils/filterHelpers';
import { searchJmesPath } from '../../../utils';
import { uniqBy } from 'lodash';

export interface IModelInstancesOptions {
  modelIds: string[];
  filter?: object; // filter to apply to the model instances
  searchableFields?: string[]; // fields to search in the model instances
  transformer: string; // jmespath expression
}

interface IProps {
  options?: ISelectOption[];
  modelInstanceOptions?: IModelInstanceOptions;
  modelInstancesOptions?: IModelInstancesOptions;
  isVisible: boolean;
  context?: Record<string, any>;
}

/**
 * Fetches and derives the selectable options for a filter, supporting static
 * options, model-instance filter values, or model-instances cursor pagination.
 * Shared by Filter and FilterGroup's child pickers.
 */
const useFilterOptions = ({
  options,
  modelInstanceOptions,
  modelInstancesOptions,
  isVisible,
  context = {},
}: IProps) => {
  const [search, setSearch] = useState<string>('');
  const [searchPath, setSearchPath] = useState<string>();
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 500);

  const { filterValues, isFetchingFilterValues } =
    useFetchModelInstancesFilterValues({
      params: {
        modelIds: modelInstanceOptions?.modelIds || [],
        fieldPath: modelInstanceOptions?.filterPath?.replace(/\[\]/g, '') || '',
        filters: buildFiltersParam({
          staticFilters: [modelInstanceOptions?.filter],
          extraFilters: [
            debouncedSearch &&
              transformKeyToJsonLogic(
                modelInstanceOptions?.searchPath || '',
                debouncedSearch,
                true
              ),
          ],
          context,
        }),
        pathFilters:
          debouncedSearch && searchPath !== undefined
            ? JSON.stringify(
                transformKeyToJsonLogic(searchPath, debouncedSearch, true)
              )
            : undefined,
        size: 20,
        page,
      },
      options: {
        enabled: !!modelInstanceOptions && isVisible,
        placeholderData: previousData => previousData,
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
    });

  const { modelInstances, isLoadingModelInstances } =
    useFetchModelInstancesCursorPagination({
      params: {
        modelIds: modelInstancesOptions?.modelIds || [],
        filters: buildFiltersParam({
          staticFilters: [modelInstancesOptions?.filter],
          extraFilters: [
            debouncedSearch &&
              buildSearchJsonLogic(
                modelInstancesOptions?.searchableFields || [],
                debouncedSearch
              ),
          ],
          context,
        }),
        size: 20,
        page,
      },
      options: {
        enabled: !!modelInstancesOptions && isVisible,
        placeholderData: previousData => previousData,
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
    });

  const {
    data: lazyLoadData,
    hasMore: lazyLoadHasMore,
    isLoading: lazyLoadIsLoading,
  } = useMemo(() => {
    if (modelInstanceOptions) {
      return {
        data: filterValues?.values,
        hasMore: filterValues?.hasNext ?? false,
        isLoading: isFetchingFilterValues,
      };
    }
    if (modelInstancesOptions) {
      return {
        data: modelInstances?.data,
        hasMore: modelInstances?.hasNext ?? false,
        isLoading: isLoadingModelInstances,
      };
    }
    return {
      data: [],
      hasMore: false,
      isLoading: false,
    };
  }, [
    modelInstanceOptions,
    modelInstancesOptions,
    filterValues,
    modelInstances,
  ]);

  const { data: accumulatedValues, onPopupScroll } = useLazyLoad({
    pageData: lazyLoadData,
    hasMore: lazyLoadHasMore,
    isLoading: lazyLoadIsLoading,
    resetKey: debouncedSearch,
    onLoadMore: () => setPage(p => p + 1),
  });

  useEffect(() => {
    if (searchPath !== undefined) {
      return;
    }

    if (filterValues && modelInstanceOptions?.searchPath) {
      if (filterValues.type === 'PRIMITIVE') {
        setSearchPath('');
      }
      if (filterValues.type === 'OBJECT') {
        // pathFilters are evaluated against each extracted value, so the
        // search path must be relative to filterPath, not just its last segment.
        const base = (modelInstanceOptions.filterPath ?? '').replace(
          /\[\]/g,
          ''
        );
        const full = modelInstanceOptions.searchPath.replace(/\[\]/g, '');
        setSearchPath(
          base && full.startsWith(`${base}.`)
            ? full.slice(base.length + 1)
            : full
        );
      }
    }
  }, [filterValues, searchPath, modelInstanceOptions?.searchPath]);

  const filters = useMemo(() => {
    // Model-instance-driven data sources take priority over `options` —
    // a saved filter can still carry a stale `options` array from a previous
    // "Static" configuration even after switching to a model instance source.
    const transformer =
      modelInstanceOptions?.transformer ||
      modelInstancesOptions?.transformer ||
      '';

    if (modelInstanceOptions || modelInstancesOptions) {
      if (accumulatedValues && transformer) {
        try {
          return searchJmesPath(accumulatedValues, transformer) || [];
        } catch (error) {
          return [];
        }
      }
      return [];
    }

    return options || [];
  }, [options, modelInstanceOptions, modelInstancesOptions, accumulatedValues]);

  const uniqFilters = useMemo(() => {
    return uniqBy(filters, 'value') as Array<any>;
  }, [filters]);

  const handleSearch = (value: string) => {
    setPage(1);
    setSearch(value);
  };

  return {
    filters: uniqFilters,
    isLoading: isFetchingFilterValues || isLoadingModelInstances,
    onPopupScroll,
    isLocalSearch: !modelInstanceOptions && !modelInstancesOptions,
    search,
    setSearch: handleSearch,
  };
};

export default useFilterOptions;
